/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  inject,
  signal,
  ChangeDetectionStrategy,
  OnInit,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  CUSTOM_ELEMENTS_SCHEMA,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormControl,
  FormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';

import {
  DialogRef,
  DialogService,
  ButtonComponent,
  IconComponent,
  InputComponent,
  FieldLabelComponent,
  ConfirmDialogComponent,
  AlertComponent,
} from 'nebula-ui-kit';
import {
  FormatoContableService,
  AplicarFormatoRequest,
  AplicarFormatoAsientoRequest,
  AplicarFormatoResponse,
  FormatoContableDto,
} from '../../data-access/formato-contable-modal/formato-contable.service';
import { FormatoContableModalService } from '../../data-access/formato-contable-modal/formato-contable-modal.service';
import {
  CentroCostoModalService,
  CentroCostoSelect,
} from '../../data-access/centro-costos-modal/centro-costos-modal.service';
import { CostCenterService } from '../../data-access/centro-costos-modal/centro-costos.service';

interface PreloadResult {
  title: string;
  preloadedData?: CentroCostoSelect[];
}

@Component({
  selector: 'app-formato-contable-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    IconComponent,
    InputComponent,
    FieldLabelComponent,
    AlertComponent,
  ],
  templateUrl: './formato-contable-modal.component.html',
  styleUrls: ['./formato-contable-modal.component.scss'],
})
export class FormatoContableDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(DialogRef, { optional: true });
  readonly dialogService = inject(DialogService);
  readonly fb = inject(FormBuilder);
  readonly cdr = inject(ChangeDetectorRef);
  readonly formatoContableService = inject(FormatoContableService);
  readonly formatoContableModal = inject(FormatoContableModalService);
  readonly centroCostoModal = inject(CentroCostoModalService);
  readonly costCenterService = inject(CostCenterService);
  tipoCentroCostoIdDesdeCuenta: number | null = null;
  centrosCostoPrecargados: CentroCostoSelect[] = [];
  porcentajeSeleccionado = 0;
  tipoCentroCostoSeleccionado = signal<number | null>(null);
  tiposPermitidos = signal<number[]>([]);

  @Input() editData?: any;
  @Input() tipoOperacionConceptoId?: number;
  @Input() terceroId = 0;
  @Input() regimenTercero = '';
  @Input() totalAntesImpuesto = 0;
  @Input() totalImpuestoFactura = 0;
  @Input() usarEndpointAsientos = false;
  @Input() conceptoContableId?: number;
  @Input() fechaAsiento?: string;

  @Output() saved = new EventEmitter<any>();
  @Output() formatoAplicado = new EventEmitter<AplicarFormatoResponse>();

  loading = signal(false);
  searching = signal(false);
  isEditMode = false;
  readonly destroy$ = new Subject<void>();

  mostrarAlerta = signal(false);
  alertaTipo = signal<'success' | 'error' | 'warning' | 'info'>('info');
  alertaTitulo = signal('');
  alertaMensaje = signal('');

  formatoCompleto: FormatoContableDto | null = null;
  requiereCentroCosto = signal(false);
  centroCostoHabilitado = signal(true);
  valorAmortizarHabilitado = signal(false);
  valorAmortizarRequerido = signal(false);
  valorImpuestoHabilitado = signal(false);
  valorImpuestoRequerido = signal(false);

  formatoSeleccionado = signal(false);
  centroCostoSeleccionado = signal(false);
  guardarHabilitado = signal(false);

  formatoForm: FormGroup;

  get codigoFormatoControl(): FormControl {
    return this.formatoForm.get('codigoFormato') as FormControl;
  }

  get nombreFormatoControl(): FormControl {
    return this.formatoForm.get('nombreFormato') as FormControl;
  }

  get codigoCentroCostoControl(): FormControl {
    return this.formatoForm.get('codigoCentroCosto') as FormControl;
  }

  get nombreCentroCostoControl(): FormControl {
    return this.formatoForm.get('nombreCentroCosto') as FormControl;
  }

  get valorBaseControl(): FormControl {
    return this.formatoForm.get('valorBase') as FormControl;
  }

  get valorAmortizarControl(): FormControl {
    return this.formatoForm.get('valorAmortizar') as FormControl;
  }

  get valorImpuestoControl(): FormControl {
    return this.formatoForm.get('valorImpuesto') as FormControl;
  }

  get valorTotalControl(): FormControl {
    return this.formatoForm.get('valorTotal') as FormControl;
  }

  private numericStringValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value === null || value === undefined || value === '') {
        return null;
      }

      const regex = /^-?\d{1,3}(\.\d{3})*(,\d{1,2})?$|^-?\d+(,\d{1,2})?$/;
      if (!regex.test(value)) {
        return { invalidFormat: true };
      }

      return null;
    };
  }

  private maxDecimalsValidator(decimals: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value === null || value === undefined || value === '') {
        return null;
      }

      const strValue = value.toString();
      const decimalPart = strValue.split(',')[1];

      if (decimalPart && decimalPart.length > decimals) {
        return {
          maxDecimals: {
            required: decimals,
            actual: decimalPart.length,
          },
        };
      }

      return null;
    };
  }

  private nonNegativeValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value === null || value === undefined || value === '') {
        return null;
      }

      const numValue = this.parseToNumber(value);
      if (!Number.isNaN(numValue) && numValue < 0) {
        return { negative: true };
      }

      return null;
    };
  }

  constructor() {
    this.formatoForm = this.fb.group({
      codigoFormato: [
        { value: '', disabled: true },
        [Validators.required, Validators.maxLength(10)],
      ],
      nombreFormato: [{ value: '', disabled: true }],
      codigoCentroCosto: [{ value: '', disabled: false }, [Validators.maxLength(10)]],
      nombreCentroCosto: [{ value: '', disabled: true }],
      valorBase: [
        '',
        [
          Validators.required,
          this.numericStringValidator(),
          this.maxDecimalsValidator(2),
          this.nonNegativeValidator(),
        ],
      ],
      valorAmortizar: [
        { value: '0,00', disabled: true },
        [this.numericStringValidator(), this.maxDecimalsValidator(2), this.nonNegativeValidator()],
      ],
      valorImpuesto: [
        '',
        [this.numericStringValidator(), this.maxDecimalsValidator(2), this.nonNegativeValidator()],
      ],
      valorTotal: [{ value: '0,00', disabled: true }, [Validators.required]],
    });

    this.codigoCentroCostoControl.disable();

    this.formatoForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.actualizarGuardarHabilitado();
    });

    this.valorBaseControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.calcularTotal());
    this.valorImpuestoControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.calcularTotal());
  }

  ngOnInit(): void {
    this.aplicarDatosDialogo();
    this.isEditMode = !!this.editData;

    const valorBaseInicial =
      this.totalAntesImpuesto > 0 ? this.formatNumber(this.totalAntesImpuesto) : '';
    this.valorBaseControl.setValue(valorBaseInicial, { emitEvent: true });
    this.valorImpuestoControl.setValue(
      this.totalImpuestoFactura > 0 ? this.formatNumber(this.totalImpuestoFactura) : '0,00',
      { emitEvent: true },
    );

    this.configurarValorImpuestoPorRegimen();
    this.actualizarGuardarHabilitado();

    if (this.editData) {
      this.cargarDatosEdicion();
      this.codigoFormatoControl.enable();
      this.codigoFormatoControl.setValue(this.editData.codigoFormato || '');
      this.codigoFormatoControl.disable();

      if (this.editData.codigoCentroCosto) {
        this.codigoCentroCostoControl.enable();
        this.codigoCentroCostoControl.setValue(this.editData.codigoCentroCosto || '');
        this.codigoCentroCostoControl.disable();
        this.centroCostoSeleccionado.set(true);
      }

      this.formatoSeleccionado.set(!!this.editData.codigoFormato);
    }
  }

  private aplicarDatosDialogo(): void {
    const data = (this.dialogRef as { data?: Record<string, unknown> } | null)?.data;
    if (!data) return;

    if (data['editData'] !== undefined) this.editData = data['editData'];
    if (data['tipoOperacionConceptoId'] != null) {
      this.tipoOperacionConceptoId = Number(data['tipoOperacionConceptoId']);
    }
    if (data['terceroId'] != null) this.terceroId = Number(data['terceroId']);
    if (data['regimenTercero'] != null) this.regimenTercero = String(data['regimenTercero']);
    if (data['totalAntesImpuesto'] != null) {
      this.totalAntesImpuesto = Number(data['totalAntesImpuesto']);
    }
    if (data['totalImpuestoFactura'] != null) {
      this.totalImpuestoFactura = Number(data['totalImpuestoFactura']);
    }
    if (data['usarEndpointAsientos'] != null) {
      this.usarEndpointAsientos = Boolean(data['usarEndpointAsientos']);
    }
    if (data['conceptoContableId'] != null) {
      this.conceptoContableId = Number(data['conceptoContableId']);
    }
    if (data['fechaAsiento'] != null) {
      this.fechaAsiento = String(data['fechaAsiento']);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isFormularioValido(): boolean {
    const rawValue = this.formatoForm.getRawValue();

    if (!rawValue.codigoFormato) return false;

    if (this.requiereCentroCosto() && !rawValue.codigoCentroCosto) return false;

    const valorBaseNum = this.parseToNumber(rawValue.valorBase);
    if (valorBaseNum <= 0) return false;

    const valorAmortizarNum = this.parseToNumber(rawValue.valorAmortizar);
    if (
      this.valorAmortizarHabilitado() &&
      this.valorAmortizarRequerido() &&
      (!rawValue.valorAmortizar || valorAmortizarNum <= 0)
    )
      return false;

    const valorImpuestoNum = this.parseToNumber(rawValue.valorImpuesto);
    if (
      this.valorImpuestoHabilitado() &&
      this.valorImpuestoRequerido() &&
      (!rawValue.valorImpuesto || valorImpuestoNum <= 0)
    )
      return false;

    if (!this.isEditMode && !this.formatoSeleccionado()) return false;

    return true;
  }

  private actualizarGuardarHabilitado(): void {
    this.guardarHabilitado.set(this.isFormularioValido());
    this.cdr.markForCheck();
  }

  parseToNumber(formattedValue: any): number {
    if (formattedValue === null || formattedValue === undefined || formattedValue === '') return 0;

    if (typeof formattedValue === 'number' && !Number.isNaN(formattedValue)) return formattedValue;

    let strValue = formattedValue.toString();

    strValue = strValue.replace(/\./g, '');
    strValue = strValue.replace(/,/g, '.');
    strValue = strValue.replace(/[^\d.-]/g, '');

    const numValue = Number.parseFloat(strValue);
    return Number.isNaN(numValue) ? 0 : numValue;
  }

  formatNumber(value: any): string {
    if (value === null || value === undefined || value === '') return '0,00';

    let numValue: number;

    if (typeof value === 'number') {
      numValue = value;
    } else {
      numValue = this.parseToNumber(value);
    }

    if (isNaN(numValue)) return '0,00';

    return numValue.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  onValorFocus(control: FormControl): void {
    const numValue = this.parseToNumber(control.value);
    if (numValue === 0) {
      control.setValue('', { emitEvent: true });
    }
    this.actualizarGuardarHabilitado();
  }

  onValueBlur(control: FormControl): void {
    if (control.value && control.value !== '') {
      const numValue = this.parseToNumber(control.value);
      const roundedValue = Math.round(numValue * 100) / 100;
      const formattedValue = this.formatNumber(roundedValue);
      if (control.value !== formattedValue) {
        control.setValue(formattedValue, { emitEvent: true });
        control.updateValueAndValidity();
      }
    }
    this.actualizarGuardarHabilitado();
  }

  private configurarValorImpuestoPorRegimen(): void {
    const esOrdinario = this.regimenTercero === 'Ordinario';

    this.valorImpuestoHabilitado.set(esOrdinario);
    this.valorImpuestoRequerido.set(esOrdinario);

    if (esOrdinario) {
      this.valorImpuestoControl.enable();
      this.valorImpuestoControl.setValidators([
        Validators.required,
        this.numericStringValidator(),
        this.maxDecimalsValidator(2),
        this.nonNegativeValidator(),
      ]);

      const currentValue = this.valorImpuestoControl.value;
      if (currentValue && currentValue !== '' && currentValue !== '0,00') {
        const numValue = this.parseToNumber(currentValue);
        this.valorImpuestoControl.setValue(this.formatNumber(numValue), { emitEvent: true });
      } else if (this.totalImpuestoFactura > 0) {
        this.valorImpuestoControl.setValue(this.formatNumber(this.totalImpuestoFactura), {
          emitEvent: true,
        });
      }
    } else {
      this.valorImpuestoControl.disable();
      this.valorImpuestoControl.setValue('0,00', { emitEvent: true });
      this.valorImpuestoControl.clearValidators();
    }
    this.valorImpuestoControl.updateValueAndValidity();
    this.calcularTotal();
  }

  private async cargarFormatoCompleto(formatoId: number): Promise<void> {
    const formatoCompleto = await firstValueFrom(this.formatoContableService.getById(formatoId));
    this.formatoCompleto = formatoCompleto ?? null;

    const tiposUnicos = this.extraerTiposCentroCostoUnicos(formatoCompleto);
    this.tiposPermitidos.set(tiposUnicos);

    if (tiposUnicos.length === 1) {
      this.tipoCentroCostoSeleccionado.set(tiposUnicos[0]);
    } else {
      this.tipoCentroCostoSeleccionado.set(null);
    }

    const tieneDistribucionCc = (this.formatoCompleto?.distribucionCc?.length ?? 0) > 0;

    if (tieneDistribucionCc) {
      this.centrosCostoPrecargados = this.formatoCompleto!.distribucionCc.map((cc) => ({
        id: cc.centroCostoId,
        codigo: cc.centroCostoCodigo || '',
        nombre: cc.centroCostoNombre || '',
        porcentaje: cc.porcentaje,
        idTipoCentroCostos: cc.tipoCentroCostoId ?? undefined,
        nombreTipoCentroCostos: cc.tipoCentroCostoNombre ?? undefined,
        status: 'ACTIVE',
      }));

      this.requiereCentroCosto.set(true);
      this.centroCostoHabilitado.set(true);
      this.centroCostoSeleccionado.set(false);

      this.codigoCentroCostoControl.disable();
      this.codigoCentroCostoControl.setValue('');
      this.nombreCentroCostoControl.setValue('');

      const opciones = this.centrosCostoPrecargados
        .map((cc) => `${cc.codigo} - ${cc.nombre}`)
        .join('\n');
      this.mostrarAlertaInfo(
        `Este formato requiere seleccionar un centro de costo de la lista predefinida.\nOpciones disponibles:\n${opciones}`,
      );
    } else {
      this.centrosCostoPrecargados = [];
      this.requiereCentroCosto.set(true);
      this.centroCostoHabilitado.set(true);
      this.centroCostoSeleccionado.set(false);

      this.codigoCentroCostoControl.enable();
      this.codigoCentroCostoControl.setValue('');
      this.nombreCentroCostoControl.setValue('');
    }

    const tieneCuentaAmortizacion = this.formatoTieneCuentaAmortizacion(this.formatoCompleto);
    this.valorAmortizarHabilitado.set(tieneCuentaAmortizacion);
    this.valorAmortizarRequerido.set(tieneCuentaAmortizacion);

    if (tieneCuentaAmortizacion) {
      this.valorAmortizarControl.enable();
      this.valorAmortizarControl.setValidators([
        Validators.required,
        this.numericStringValidator(),
        this.maxDecimalsValidator(2),
        this.nonNegativeValidator(),
      ]);
    } else {
      this.valorAmortizarControl.disable();
      this.valorAmortizarControl.setValue('0,00', { emitEvent: false });
      this.valorAmortizarControl.clearValidators();
    }
    this.valorAmortizarControl.updateValueAndValidity();

    this.cdr.markForCheck();
    this.actualizarGuardarHabilitado();
  }

  private formatoTieneCuentaAmortizacion(formato: FormatoContableDto | null): boolean {
    return (
      formato?.cuentasContables?.some(
        (cuenta) =>
          cuenta.cuentaAmortizacionAnticipoId != null && cuenta.cuentaAmortizacionAnticipoId > 0,
      ) ?? false
    );
  }

  private extraerTiposCentroCostoUnicos(formato: FormatoContableDto | null): number[] {
    if (!formato?.cuentasContables) return [];
    const tiposSet = new Set<number>();
    formato.cuentasContables.forEach((cuenta) => {
      if (cuenta.tipoCentroCostoId !== null && cuenta.tipoCentroCostoId !== undefined) {
        tiposSet.add(cuenta.tipoCentroCostoId);
      }
    });
    return Array.from(tiposSet);
  }

  private calcularTotal(): void {
    const valorBaseRaw = this.valorBaseControl.value;
    const valorImpuestoRaw = this.valorImpuestoControl.value;

    const valorBase = this.parseToNumber(valorBaseRaw);
    const valorImpuesto = this.parseToNumber(valorImpuestoRaw);

    const valorTotal = valorBase + valorImpuesto;
    const valorTotalFormatted = this.formatNumber(valorTotal);

    if (this.valorTotalControl.value !== valorTotalFormatted) {
      this.valorTotalControl.setValue(valorTotalFormatted, { emitEvent: false });
      this.cdr.detectChanges();
    }
  }

  private cargarDatosEdicion(): void {
    if (!this.editData) return;

    this.valorBaseControl.enable();
    this.valorImpuestoControl.enable();
    if (this.valorAmortizarHabilitado()) {
      this.valorAmortizarControl.enable();
    }

    this.formatoForm.patchValue({
      codigoFormato: this.editData.codigoFormato || '',
      nombreFormato: this.editData.nombreFormato || '',
      codigoCentroCosto: this.editData.codigoCentroCosto || '',
      nombreCentroCosto: this.editData.nombreCentroCosto || '',
      valorBase: this.formatNumber(this.editData.valorBase),
      valorAmortizar: this.formatNumber(this.editData.valorAmortizar),
      valorImpuesto: this.formatNumber(this.editData.valorImpuesto),
      valorTotal: this.formatNumber(this.editData.valorTotal),
    });

    this.valorBaseControl.disable();
    if (!this.valorImpuestoHabilitado()) {
      this.valorImpuestoControl.disable();
    }
    if (!this.valorAmortizarHabilitado()) {
      this.valorAmortizarControl.disable();
    }
  }

  getError(fieldName: string): string {
    const control = this.formatoForm.get(fieldName);
    if (!control || !control.errors || !control.touched) return '';

    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['maxDecimals'])
      return `Solo se permiten máximo ${control.errors['maxDecimals'].required} decimales`;
    if (control.errors['invalidFormat'])
      return 'Formato inválido. Use punto para miles y coma para decimales (ej: 1.234,56)';
    if (control.errors['negative']) return 'No se permiten valores negativos';
    if (control.errors['min']) return `El valor mínimo es ${control.errors['min'].min}`;

    return 'Campo inválido';
  }

  async buscarFormato(): Promise<void> {
    if (this.loading() || this.isEditMode) return;

    this.searching.set(true);

    try {
      const formato = await firstValueFrom(
        this.formatoContableModal.openSelector(
          'Seleccionar Formato Contable',
          this.codigoFormatoControl.value,
          {
            status: 'ACTIVE',
          },
        ),
      );

      if (formato) {
        this.codigoFormatoControl.enable();
        this.codigoFormatoControl.setValue(formato.codigo);
        this.codigoFormatoControl.disable();

        this.nombreFormatoControl.setValue(formato.nombre);
        await this.cargarFormatoCompleto(formato.id);
        this.formatoSeleccionado.set(true);
        this.actualizarGuardarHabilitado();
        this.cdr.markForCheck();
      }
    } catch (error) {
      this.mostrarAlertaError('Error al buscar formatos contables');
    } finally {
      this.searching.set(false);
    }
  }

  async buscarCentroCosto(): Promise<void> {
    if (this.loading() || this.isEditMode) return;

    if (!this.formatoSeleccionado()) {
      this.mostrarAlertaError('Debe seleccionar un formato contable primero');
      return;
    }

    this.searching.set(true);
    this.cerrarAlerta();

    try {
      const preloadResult = await this.preloadCentrosCosto();
      if (!preloadResult) return;

      const centroCosto = await this.openSelectorDialog(preloadResult);
      if (!centroCosto) return;

      if (!(await this.validateCentroCostoSelection(centroCosto, preloadResult))) return;

      this.applyCentroCostoSelection(centroCosto);
    } catch (error) {
      console.error('Error al buscar centro de costo:', error);
      this.mostrarAlertaError('Error al buscar centro de costo');
    } finally {
      this.searching.set(false);
    }
  }

  private async preloadCentrosCosto(): Promise<PreloadResult | null> {
    if (this.centrosCostoPrecargados.length > 0) {
      const title = `Seleccionar Centro de Costo (${this.centrosCostoPrecargados.length} opción(es) disponible(s))`;
      this.logAvailableOptions(this.centrosCostoPrecargados);
      return { title, preloadedData: this.centrosCostoPrecargados };
    }

    if (this.tipoCentroCostoSeleccionado() !== null) {
      const todosLosCentros = await firstValueFrom(this.costCenterService.getActiveCentros());

      const preloadedData = todosLosCentros.filter(
        (centro) =>
          Number(centro.idTipoCentroCostos) === Number(this.tipoCentroCostoSeleccionado()),
      );

      if (preloadedData.length === 0) {
        this.mostrarAlertaError('No hay centros de costo activos del tipo seleccionado');
        return null;
      }

      const title = `Seleccionar Centro de Costo (${preloadedData.length} opción(es) disponible(s))`;
      return { title, preloadedData };
    }

    return { title: 'Seleccionar Centro de Costo', preloadedData: undefined };
  }

  private async openSelectorDialog(
    preloadResult: PreloadResult,
  ): Promise<CentroCostoSelect | null> {
    return await firstValueFrom(
      this.centroCostoModal.openSelector(
        preloadResult.title,
        this.codigoCentroCostoControl.value,
        { status: 'ACTIVE' },
        preloadResult.preloadedData,
      ),
    );
  }

  private async validateCentroCostoSelection(
    centroCosto: CentroCostoSelect,
    preloadResult: PreloadResult,
  ): Promise<boolean> {
    if (preloadResult.preloadedData && preloadResult.preloadedData.length > 0) {
      const esPermitido = preloadResult.preloadedData.some(
        (cc) => cc.id === centroCosto.id || cc.codigo === centroCosto.codigo,
      );

      if (!esPermitido) {
        this.mostrarAlertaError(
          'El centro de costo seleccionado no está permitido para este formato',
        );
        return false;
      }

      return true;
    }

    if (this.tipoCentroCostoSeleccionado() !== null) {
      const esCompatible = await this.validarCompatibilidadTipoCentroCosto(
        centroCosto.id,
        this.tipoCentroCostoSeleccionado(),
        centroCosto.idTipoCentroCostos,
      );

      if (!esCompatible) {
        this.mostrarAlertaError(
          'El centro de costo ingresado no pertenece a los tipos de centro de costos definido en el Formato Contable',
        );
        return false;
      }
    }

    return true;
  }

  private applyCentroCostoSelection(centroCosto: CentroCostoSelect): void {
    this.cerrarAlerta();
    this.codigoCentroCostoControl.setValue(centroCosto.codigo);
    this.nombreCentroCostoControl.setValue(centroCosto.nombre);
    this.centroCostoSeleccionado.set(true);

    if (this.centrosCostoPrecargados.length > 0) {
      const distribucion = this.centrosCostoPrecargados.find((cc) => cc.id === centroCosto.id);
      if (distribucion && 'porcentaje' in distribucion) {
        this.porcentajeSeleccionado = (distribucion as any).porcentaje;
        this.mostrarAlertaInfo(
          `Centro seleccionado: ${centroCosto.codigo} - ${centroCosto.nombre} (${this.porcentajeSeleccionado}%)`,
        );
      }
    }

    this.cdr.markForCheck();
    this.actualizarGuardarHabilitado();
  }

  private logAvailableOptions(centrosCosto: CentroCostoSelect[]): void {
    const opcionesTexto = centrosCosto.map((cc) => `${cc.codigo} - ${cc.nombre}`).join(' | ');
  }

  private async validarCompatibilidadTipoCentroCosto(
    centroCostoId: number,
    tipoSeleccionado: number | null,
    tipoDesdeSeleccion?: number | null,
  ): Promise<boolean> {
    if (tipoSeleccionado === null) return true;

    if (tipoDesdeSeleccion != null && Number(tipoDesdeSeleccion) === Number(tipoSeleccionado)) {
      return true;
    }

    const centroCompleto = await firstValueFrom(
      this.costCenterService.getCentroById(centroCostoId),
    );
    const tipoRealCentro = centroCompleto?.idTipoCentroCostos;
    return Number(tipoRealCentro) === Number(tipoSeleccionado);
  }

  private async validarFormulario(
    formValue: any,
  ): Promise<{ isValid: boolean; errorMsg?: string }> {
    const valorAmortizar = this.parseToNumber(formValue.valorAmortizar);
    const valorBase = this.parseToNumber(formValue.valorBase);
    const valorImpuesto = this.parseToNumber(formValue.valorImpuesto);

    if (valorAmortizar > valorBase) {
      return { isValid: false, errorMsg: 'El valor amortizar no puede ser mayor al valor base' };
    }

    if (this.regimenTercero === 'Ordinario' && (!formValue.valorImpuesto || valorImpuesto <= 0)) {
      return {
        isValid: false,
        errorMsg: 'El valor impuesto es obligatorio para terceros con régimen ordinario',
      };
    }

    return { isValid: true };
  }

  private async obtenerFormatoId(codigoFormato: string): Promise<number> {
    const formatos = await firstValueFrom(
      this.formatoContableService.searchFormatos(codigoFormato),
    );
    const formatoId = formatos?.[0]?.id;
    if (!formatoId) {
      throw new Error('No se encontró el formato contable seleccionado');
    }
    return formatoId;
  }

  private async obtenerCentroCostoData(
    codigoCentroCosto: string,
  ): Promise<{ id: number; tipoId?: number } | null> {
    if (!codigoCentroCosto) return null;

    const centros = await firstValueFrom(this.costCenterService.searchCentros(codigoCentroCosto));
    const centroCostoId = centros?.[0]?.id;
    const tipoCentroCostoId = centros?.[0]?.idTipoCentroCostos;

    if (!centroCostoId) {
      throw new Error('No se encontró el centro de costo seleccionado');
    }

    return { id: centroCostoId, tipoId: tipoCentroCostoId ?? undefined };
  }

  private async validarReglasNegocio(): Promise<{ isValid: boolean; errorMsg?: string }> {
    if (!this.formatoCompleto) {
      return { isValid: false, errorMsg: 'No se ha cargado la configuración del formato' };
    }

    const tieneDistribucionCc = (this.formatoCompleto.distribucionCc?.length ?? 0) > 0;
    const tieneCuentaAmortizacion = this.formatoTieneCuentaAmortizacion(this.formatoCompleto);

    const formValue = this.formatoForm.getRawValue();

    if (tieneDistribucionCc && !formValue.codigoCentroCosto) {
      return {
        isValid: false,
        errorMsg:
          'Debe seleccionar un centro de costo de las opciones disponibles para este formato',
      };
    }

    if (!tieneDistribucionCc && !formValue.codigoCentroCosto && this.requiereCentroCosto()) {
      return {
        isValid: false,
        errorMsg: 'El centro de costo es obligatorio para este formato contable',
      };
    }

    if (
      tieneCuentaAmortizacion &&
      (!formValue.valorAmortizar || this.parseToNumber(formValue.valorAmortizar) <= 0)
    ) {
      return { isValid: false, errorMsg: 'El valor a amortizar es obligatorio para este formato' };
    }

    return { isValid: true };
  }

  async onSave(): Promise<void> {
    if (!this.isFormularioValido()) {
      this.handleValidationErrors();
      return;
    }

    this.loading.set(true);

    try {
      const formValue = this.prepareFormValue();

      if (!this.validateFormValues(formValue)) {
        return;
      }

      const formatoId = await this.ensureFormatoLoaded(formValue.codigoFormato);

      if (!(await this.validateBusinessRules())) {
        return;
      }

      const resultado = this.usarEndpointAsientos
        ? await this.executeSaveRequest(await this.buildRequestAsiento(formValue))
        : await this.executeSaveRequest(await this.buildRequestCausacion(formValue, formatoId));

      this.handleSuccess(resultado, formValue);
    } catch (error: any) {
      this.handleSaveError(error);
    } finally {
      this.loading.set(false);
    }
  }

  private handleValidationErrors(): void {
    if (!this.formatoSeleccionado()) {
      this.mostrarAlertaError('Debe seleccionar un formato contable usando el botón de búsqueda');
    } else if (this.requiereCentroCosto() && !this.centroCostoSeleccionado()) {
      this.mostrarAlertaError('Debe seleccionar un centro de costo usando el botón de búsqueda');
    } else {
      this.mostrarAlertaError('Por favor complete todos los campos obligatorios');
    }
  }

  private prepareFormValue(): any {
    const rawValue = this.formatoForm.getRawValue();

    const valorBaseNum = this.parseToNumber(rawValue.valorBase);
    const valorAmortizarNum = this.parseToNumber(rawValue.valorAmortizar);
    const valorImpuestoNum = this.parseToNumber(rawValue.valorImpuesto);
    const valorTotalNum = this.parseToNumber(rawValue.valorTotal);

    return {
      ...rawValue,
      valorBase: valorBaseNum,
      valorAmortizar: valorAmortizarNum,
      valorImpuesto: valorImpuestoNum,
      valorTotal: valorTotalNum,
      codigoFormato: rawValue.codigoFormato,
      codigoCentroCosto: rawValue.codigoCentroCosto,
    };
  }

  private validateFormValues(formValue: any): boolean {
    if (formValue.valorAmortizar > formValue.valorBase) {
      this.mostrarAlertaError('El valor amortizar no puede ser mayor al valor base');
      return false;
    }

    if (
      this.regimenTercero === 'Ordinario' &&
      (!formValue.valorImpuesto || formValue.valorImpuesto <= 0)
    ) {
      this.mostrarAlertaError(
        'El valor impuesto es obligatorio para terceros con régimen ordinario',
      );
      return false;
    }

    return true;
  }

  private async ensureFormatoLoaded(codigoFormato: string): Promise<number> {
    const formatoId = await this.obtenerFormatoId(codigoFormato);

    if (!this.formatoCompleto || this.formatoCompleto.id !== formatoId) {
      await this.cargarFormatoCompleto(formatoId);
    }

    return formatoId;
  }

  private async validateBusinessRules(): Promise<boolean> {
    const reglasNegocio = await this.validarReglasNegocio();

    if (!reglasNegocio.isValid) {
      this.mostrarAlertaError(reglasNegocio.errorMsg!);
      return false;
    }

    return true;
  }

  private async resolveCentroCostoInfo(
    codigoCentroCosto: string,
  ): Promise<{ id: number; tipoId?: number } | null> {
    if (!codigoCentroCosto) return null;

    if (this.centrosCostoPrecargados.length > 0) {
      const centroSeleccionado = this.centrosCostoPrecargados.find(
        (cc) => cc.codigo === codigoCentroCosto,
      );

      if (centroSeleccionado) {
        return {
          id: centroSeleccionado.id,
          tipoId:
            centroSeleccionado.idTipoCentroCostos ??
            this.tipoCentroCostoSeleccionado() ??
            undefined,
        };
      }
    }

    const centroInfo = await this.obtenerCentroCostoData(codigoCentroCosto);
    if (!centroInfo) return null;

    const esCompatible = await this.validarCompatibilidadTipoCentroCosto(
      centroInfo.id,
      this.tipoCentroCostoSeleccionado(),
      centroInfo.tipoId,
    );

    if (!esCompatible) {
      throw new Error(
        'El centro de costo ingresado no pertenece a los tipos de centro de costos definido en el Formato Contable',
      );
    }

    return centroInfo;
  }

  private async buildRequestCausacion(
    formValue: any,
    formatoId: number,
  ): Promise<AplicarFormatoRequest> {
    const centroData = formValue.codigoCentroCosto
      ? await this.resolveCentroCostoInfo(formValue.codigoCentroCosto)
      : null;

    return {
      formatoContableId: formatoId,
      terceroId: this.terceroId || 1,
      valorBase: formValue.valorBase,
      valorImpuesto: formValue.valorImpuesto,
      valorAmortizar: formValue.valorAmortizar,
      tipoCentroCostoId: centroData?.tipoId ?? this.tipoCentroCostoSeleccionado() ?? undefined,
      ...(centroData ? { centroCostosId: centroData.id } : {}),
    };
  }

  private async buildRequestAsiento(formValue: any): Promise<AplicarFormatoAsientoRequest> {
    if (!this.terceroId || this.terceroId <= 0) {
      throw new Error('Debe seleccionar un tercero válido en la cabecera del asiento');
    }

    if (!this.conceptoContableId || this.conceptoContableId <= 0) {
      throw new Error('Debe seleccionar un concepto contable en la cabecera del asiento');
    }

    if (!this.fechaAsiento) {
      throw new Error('Debe ingresar la fecha del asiento en la cabecera');
    }

    const centroData = formValue.codigoCentroCosto
      ? await this.resolveCentroCostoInfo(formValue.codigoCentroCosto)
      : null;

    const tipoCentroCostosId =
      centroData?.tipoId ?? this.tipoCentroCostoSeleccionado() ?? undefined;

    return {
      formatoContable: formValue.codigoFormato,
      ...(tipoCentroCostosId != null ? { tipoCentroCostosId } : {}),
      ...(formValue.codigoCentroCosto ? { codigoCentroCosto: formValue.codigoCentroCosto } : {}),
      valorAntesImpuesto: formValue.valorBase,
      valorImpuesto: formValue.valorImpuesto ?? 0,
      valorAmortizar: formValue.valorAmortizar ?? 0,
      terceroId: this.terceroId,
      conceptoContableId: this.conceptoContableId,
      fecha: this.fechaAsiento,
    };
  }

  private async executeSaveRequest(
    request: AplicarFormatoRequest | AplicarFormatoAsientoRequest,
  ): Promise<any> {
    const aplicar$ = this.usarEndpointAsientos
      ? this.formatoContableService.aplicarFormatoAsiento(request as AplicarFormatoAsientoRequest)
      : this.formatoContableService.aplicarFormato(request as AplicarFormatoRequest);

    return await firstValueFrom(aplicar$);
  }

  private normalizeLineasAsiento(lineas: any[]): any[] {
    return lineas.map((linea) => {
      const naturaleza = String(linea.naturaleza || '').toUpperCase();
      const valor = Number(linea.valor ?? 0);

      return {
        ...linea,
        cuentaContableId: linea.cuentaContableId,
        cuentaCodigo: linea.cuentaCodigo || linea.cuentaContableCodigo || '',
        cuentaNombre: linea.cuentaNombre || linea.cuentaContableNombre || '',
        valorDebito: Number(linea.valorDebito ?? (naturaleza === 'DEBITO' ? valor : 0)),
        valorCredito: Number(linea.valorCredito ?? (naturaleza === 'CREDITO' ? valor : 0)),
        centroCostosId: linea.centroCostosId ?? linea.centroCostoId ?? null,
        centroCostosCodigo: linea.centroCostosCodigo || linea.centroCostoCodigo || '',
        terceroId: linea.terceroId ?? null,
        terceroNombre: linea.terceroNombre || '',
        baseDeduccion: linea.baseDeduccion ?? linea.base ?? 0,
      };
    });
  }

  private handleSuccess(resultado: any, formValue: any): void {
    const lineasRaw =
      resultado?.lineasAsiento ||
      resultado?.lineas ||
      resultado?.response?.content?.lineasAsiento ||
      resultado?.response?.content?.lineas ||
      resultado?.content?.lineasAsiento ||
      resultado?.content?.lineas ||
      [];

    if (lineasRaw.length > 0) {
      const lineasAsiento = this.normalizeLineasAsiento(lineasRaw);
      const totalDebito = lineasAsiento.reduce((sum, linea) => sum + (linea.valorDebito || 0), 0);
      const totalCredito = lineasAsiento.reduce((sum, linea) => sum + (linea.valorCredito || 0), 0);

      this.mostrarAlertaExito(
        `Asiento contable calculado correctamente. Total Débito: ${this.formatCurrency(totalDebito)}, Total Crédito: ${this.formatCurrency(totalCredito)}`,
      );

      this.formatoAplicado.emit(resultado);
      this.saved.emit(resultado);

      this.dialogRef?.close({
        success: true,
        resultado,
        datosFormulario: formValue,
        asientoContable: lineasAsiento,
      });
      return;
    }

    this.mostrarAlertaExito('Formato contable aplicado correctamente');

    this.formatoAplicado.emit(resultado);
    this.saved.emit(resultado);

    this.dialogRef?.close({
      success: true,
      resultado,
      datosFormulario: formValue,
    });
  }

  private handleSaveError(error: any): void {
    this.mostrarAlertaError(this.extractApiErrorMessage(error));
  }

  private extractApiErrorMessage(error: any): string {
    const apiError = error?.error;

    if (typeof apiError === 'string' && apiError.trim()) {
      return apiError;
    }

    if (apiError?.message) {
      return apiError.message;
    }

    if (Array.isArray(apiError?.errors) && apiError.errors.length > 0) {
      return apiError.errors
        .map((item: { message?: string }) => item.message)
        .filter(Boolean)
        .join('. ');
    }

    return error?.message || 'Error al aplicar el formato contable';
  }

  async onCancel(): Promise<void> {
    if (this.formatoForm.dirty && !this.isEditMode) {
      const confirmed = await this.showConfirmDialog();
      if (!confirmed) {
        return;
      }
    }
    this.dialogRef?.close(null);
  }

  private async showConfirmDialog(): Promise<boolean> {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      data: {
        title: '¿Cancelar cambios?',
        message: 'Tienes cambios sin guardar. ¿Estás seguro de que deseas cancelar?',
        type: 'warning',
        confirmText: 'Sí, cancelar',
        cancelText: 'No, seguir editando',
      },
      size: 'sm',
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    return result === true;
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  }

  private mostrarAlertaExito(mensaje: string): void {
    this.alertaTipo.set('success');
    this.alertaTitulo.set('Éxito');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);

    setTimeout(() => {
      this.cerrarAlerta();
    }, 5000);
  }

  private mostrarAlertaError(mensaje: string): void {
    this.alertaTipo.set('error');
    this.alertaTitulo.set('Error');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);
  }

  private mostrarAlertaWarning(mensaje: string): void {
    this.alertaTipo.set('warning');
    this.alertaTitulo.set('Advertencia');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);
  }

  private mostrarAlertaInfo(mensaje: string): void {
    this.alertaTipo.set('info');
    this.alertaTitulo.set('Información');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);

    setTimeout(() => {
      this.cerrarAlerta();
    }, 5000);
  }

  cerrarAlerta(): void {
    this.mostrarAlerta.set(false);
  }
}
